import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, take } from 'rxjs';

import { CustomDialogService } from './custom-dialog.service';
import {
  KlDesignPreferenceDynamicComponent,
  type KlDesignPreferenceResult,
} from '@shared/components/kl-design-preference-dynamic/kl-design-preference-dynamic.component';

export type { KlDesignPreferenceResult };

export interface KlDesignPreferenceDialogOpenOptions {
  modalId: string;
  commitType: 'add' | 'edit';
  currentData?: Record<string, unknown> | null;
  /** i18n key passed to `KlDesignPreferenceDynamicComponent.nameLabelKey` (e.g. `filter_name`, `design_name`). */
  nameLabelKey?: string;
  header?: string;
  width?: string;
  /** Runs save before the dialog closes; shows loading on the footer Save button. */
  submitAction?: (result: KlDesignPreferenceResult) => Observable<unknown>;
}

@Injectable({ providedIn: 'root' })
export class KlDesignPreferenceDialogService {
  private readonly customDialog = inject(CustomDialogService);
  private readonly translate = inject(TranslateService);

  open(options: KlDesignPreferenceDialogOpenOptions): Observable<KlDesignPreferenceResult | null> {
    const headerKey = options.header ?? (options.commitType === 'add' ? 'new_filter' : 'edit_design_filter');
    const header = this.translate.instant(headerKey) as string;

    const ref = this.customDialog.open({
      modalId: options.modalId,
      component: KlDesignPreferenceDynamicComponent,
      header,
      width: options.width ?? 'min(420px, 94vw)',
      styleClass: 'kl-design-preference-dialog-shell',
      baseZIndex: 12000,
      inputValues: {
        commitType: options.commitType,
        currentData: options.currentData ?? null,
        ...(options.nameLabelKey != null && options.nameLabelKey !== '' ? { nameLabelKey: options.nameLabelKey } : {}),
        ...(options.submitAction ? { submitAction: options.submitAction } : {}),
      },
      contentStyle: { overflow: 'auto' },
    });

    if (!ref) {
      return of(null);
    }

    return new Observable<KlDesignPreferenceResult | null>((subscriber) => {
      ref.onClose.pipe(take(1)).subscribe({
        next: (result) => {
          subscriber.next((result as KlDesignPreferenceResult | undefined) ?? null);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });
    });
  }
}
